import { styled } from 'styled-components';
import { QuantReportItem } from './Quant3';

interface DetailInfoModalProps {
  item: QuantReportItem;
  selectedThemes: string[];
  onClose: () => void;
}

function DetailInfoModal({
  item,
  selectedThemes,
  onClose,
}: DetailInfoModalProps) {
  const themeMap: Record<string, keyof typeof item> = {
    저평가주: 'appraisal_vs_market_price',
    공시지가보다낮주: 'bid_vs_public_land_price',
    전세가보다낮주: 'lowest_bid_vs_jeonse',
    전세우위주: 'jeonse_advantage_3y',
    월세우위주: 'wolse_advantage_3y',
    평균보다낮주: 'nearby_avg_vs_market_price',
    시세차익주: 'sale_price_vs_lowest_bid',
    저평가부실채권주: 'appraisal_vs_market_price',
    소액투자주: 'expected_equity',
    시세상승주: 'real_trade_growth_rate_1y',
    이자보다월세주: 'monthly_rent_vs_loan_interest',
  };

  const boldWords = [
    '감정가',
    '입찰가',
    '최저입찰가',
    '전세 우위',
    '월세 우위',
    '시세',
    '매각가',
    '자기자본',
    '실거래 상승율',
    '월세 수익률',
  ];

  const renderDetailTitle = (fullText: string) => {
    const boldWord = boldWords.find((word) => fullText.includes(word));
    if (!boldWord) return fullText;
    const parts = fullText.split(boldWord);
    return (
      <>
        {parts[0]} <span>{boldWord}</span> {parts[1]}
      </>
    );
  };

  const formatPercentage = (value: number) => {
    if (value === null || value === undefined || isNaN(value)) return '-';
    return `${value}%`;
  };

  const formatKoreanWon = (amount: number | string) => {
    if (typeof amount === 'string') {
      amount = parseFloat(amount.replace(/,/g, ''));
    }
    if (typeof amount !== 'number' || isNaN(amount)) return '-';
    const isNegative = amount < 0;
    amount = Math.abs(amount);

    const units = [
      { value: 100000000, label: '억 ' },
      { value: 10000, label: '만' },
    ];

    let result = '';
    for (let unit of units) {
      if (amount >= unit.value) {
        const unitCount = Math.floor(amount / unit.value);
        amount %= unit.value;
        result += unitCount + unit.label;
      }
    }

    if (!result) {
      result = amount.toLocaleString() + '원';
    }

    return isNegative ? '-' + result : result;
  };

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContainer onClick={(e) => e.stopPropagation()}>
        <div>
          <Header>
            상세 분석
            <img
              src="/assets/images/close/close-dark-gray.svg"
              alt="닫기"
              onClick={onClose}
            />
          </Header>
          {selectedThemes.map((theme, idx) => {
            const key = themeMap[theme];
            if (!key) return null;
            const titleMap: Partial<Record<string, string>> = {
              appraisal_vs_market_price: '시세 대비 감정가',
              bid_vs_public_land_price: '공시지가 대비 입찰가',
              lowest_bid_vs_jeonse: '전세가 대비 최저입찰가',
              jeonse_advantage_3y: '최근 6개월 전세 우위 비중',
              wolse_advantage_3y: '최근 6개월 월세 우위 비중',
              nearby_avg_vs_market_price: '인근 평균 시세 대비',
              sale_price_vs_lowest_bid: '최저입찰가 대비 매각가',
              expected_equity: '예상 자기자본',
              real_trade_growth_rate_1y: '최근 1년 실거래 상승율',
              monthly_rent_vs_loan_interest: '대출이자 대비 월세 수익률',
            };

            const detailLabelMap = {
              appraisal_vs_market_price: {
                label1: 'AI 추정가',
                label2: '감정가',
                diffPrefix: '시세보다 감정가가',
              },
              bid_vs_public_land_price: {
                label1: '공시지가',
                label2: '최저입찰가',
                diffPrefix: '공시지가보다 매입가가',
              },
              lowest_bid_vs_jeonse: {
                label1: '전세보증금',
                label2: '최저입찰가',
                diffPrefix: '전세가보다 최저입찰가가',
              },
              sale_price_vs_lowest_bid: {
                label1: '최저입찰가',
                label2: '매각가',
                diffPrefix: '최저입찰가보다 매각가가',
              },
              jeonse_advantage_3y: {
                label1: '월세 우위 비중',
                label2: '전세 우위 비중',
                diffPrefix: '최근 3년 월세보다 전세비중이',
              },
              wolse_advantage_3y: {
                label1: '전세 우위 비중',
                label2: '월세 우위 비중',
                diffPrefix: '최근 3년 전세보다 월세비중이',
              },
              nearby_avg_vs_market_price: {
                label1: '인근지역 평균시세',
                label2: '최저입찰가',
                diffPrefix: '인근지역 평균 시세보다 최저입찰가가',
              },
              expected_equity: {
                label1: '',
                label2: '',
                diffPrefix: '예상자기 자본',
              },
              real_trade_growth_rate_1y: {
                label1: '',
                label2: '',
                diffPrefix: '최근 6개월 실거래 상승률',
              },
              monthly_rent_vs_loan_interest: {
                label1: '월 대출이자',
                label2: '월세수익',
                diffPrefix: '월 대출이자보다 월세수익이',
              },
            };

            const labels = detailLabelMap[key as keyof typeof detailLabelMap];
            const getCompareValues = (key: string) => {
              switch (key) {
                case 'appraisal_vs_market_price':
                  return {
                    label1Value: item.ex_sale_prc,
                    label2Value: item.appraise_prc,
                  };
                case 'bid_vs_public_land_price':
                  return {
                    label1Value: item.official_prc,
                    label2Value: item.min_prc,
                  };
                case 'lowest_bid_vs_jeonse':
                  return {
                    label1Value: item.jeonse,
                    label2Value: item.min_prc,
                  };
                case 'sale_price_vs_lowest_bid':
                  return {
                    label1Value: item.min_prc,
                    label2Value: item.ex_sale_prc,
                  };
                case 'jeonse_advantage_3y':
                  return {
                    label1Value: item.wolse_advantage_3y ?? 0,
                    label2Value: item.jeonse_advantage_3y ?? 0,
                  };
                case 'wolse_advantage_3y':
                  return {
                    label1Value: item.jeonse_advantage_3y ?? 0,
                    label2Value: item.wolse_advantage_3y ?? 0,
                  };
                case 'expected_equity':
                  return { label1Value: null, label2Value: item.investment };
                case 'nearby_avg_vs_market_price':
                  return {
                    label1Value: item.surrounding_prc ?? 0,
                    label2Value: item.min_prc ?? 0,
                  };
                case 'real_trade_growth_rate_1y':
                  return {
                    label1Value: null,
                    label2Value: item.real_trade_growth_rate_1y,
                  };
                case 'monthly_rent_vs_loan_interest':
                  return {
                    label1Value: item.wolse_interest,
                    label2Value: item.wolse_profit,
                  };
                default:
                  return { label1Value: null, label2Value: null };
              }
            };

            const { label1Value, label2Value } = getCompareValues(key);
            const diff =
              label1Value !== null && label2Value !== null
                ? Number(label2Value) - Number(label1Value)
                : null;

            if (key === 'expected_equity') {
              return (
                <Row03 key={idx} style={{ padding: '8px 16px' }}>
                  <div>
                    <DetailInfoTitle>{labels.diffPrefix}</DetailInfoTitle>
                    <DetailInfoNumber>
                      {label2Value ? formatKoreanWon(label2Value) : '-'}
                    </DetailInfoNumber>
                  </div>
                </Row03>
              );
            }

            if (key === 'real_trade_growth_rate_1y') {
              const textColor =
                label2Value !== null && Number(label2Value) > 0
                  ? '#F45551'
                  : '#222';
              return (
                <Row03 key={idx} style={{ padding: '8px 16px' }}>
                  <div>
                    <DetailInfoTitle>{labels.diffPrefix}</DetailInfoTitle>
                    <DetailInfoNumber color={textColor}>
                      {label2Value
                        ? formatPercentage(Number(label2Value))
                        : '-'}
                      <img
                        src="/assets/images/arrow/arrow-up-red-fill.svg"
                        alt=""
                      />
                    </DetailInfoNumber>
                  </div>
                </Row03>
              );
            }

            return (
              <PopoverContent key={idx}>
                <>
                  <Row key={idx} style={{ justifyContent: 'space-between' }}>
                    <Row03>
                      <div>
                        <DetailInfoTitle>
                          {labels?.label1 ??
                            renderDetailTitle(titleMap[key] ?? '')}
                        </DetailInfoTitle>
                        <DetailInfoNumber color="#222">
                          {label1Value !== null && label1Value !== undefined
                            ? key === 'jeonse_advantage_3y' ||
                              key === 'wolse_advantage_3y'
                              ? formatPercentage(Number(label1Value))
                              : formatKoreanWon(label1Value)
                            : '-'}
                        </DetailInfoNumber>
                      </div>
                      <div>
                        <DetailInfoTitle>
                          {labels?.label2 ??
                            renderDetailTitle(titleMap[key] ?? '')}
                        </DetailInfoTitle>
                        <DetailInfoNumber color="#222">
                          {label2Value !== null && label2Value !== undefined
                            ? key === 'jeonse_advantage_3y' ||
                              key === 'wolse_advantage_3y'
                              ? formatPercentage(Number(label2Value))
                              : formatKoreanWon(label2Value)
                            : '-'}
                        </DetailInfoNumber>
                      </div>
                    </Row03>
                  </Row>
                  <Row04 key={`${idx}-diff`}>
                    <DetailInfoTitle>
                      {labels?.diffPrefix ?? ''}
                    </DetailInfoTitle>
                    <DetailInfoNumber02>
                      {label1Value !== null && label2Value !== null ? (
                        <>
                          {key === 'jeonse_advantage_3y' ||
                          key === 'wolse_advantage_3y'
                            ? `${Math.abs(diff!)}%`
                            : formatKoreanWon(Math.abs(diff!))}{' '}
                          <span
                            style={{
                              color: diff! > 0 ? '#F45551' : '#008AFF',
                            }}
                          >
                            {diff! > 0 ? '높음' : '낮음'}
                          </span>
                        </>
                      ) : (
                        '-'
                      )}
                    </DetailInfoNumber02>
                  </Row04>
                </>
              </PopoverContent>
            );
          })}
        </div>
      </ModalContainer>
    </ModalOverlay>
  );
}

export default DetailInfoModal;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.52);
  display: flex;
  justify-content: center;
  align-items: flex-end;
  z-index: 3000;
`;

const ModalContainer = styled.div`
  width: 100%;
  max-height: 780px;
  background-color: #ffffff;
  display: flex;
  flex-direction: column;
  margin: 0 auto;
  padding: 8px 0;
  border-radius: 24px 24px 0 0;
  border: 1px solid #e1e2e4;
  box-shadow:
    0 2px 8px 0 rgba(0, 0, 0, 0.12),
    0 1px 4px 0 rgba(0, 0, 0, 0.08),
    0 0 1px 0 rgba(0, 0, 0, 0.08);
  > div {
    height: 100%;
    overflow: hidden;
    overflow-y: scroll;
    &::-webkit-scrollbar {
      display: none;
    }
  }
`;

const Header = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  padding: 8px 16px;
  align-items: center;
  font-weight: 500;
  font-size: 16px;
  line-height: 145%;
  letter-spacing: -2%;
  img {
    cursor: pointer;
  }
`;

const Box = styled.div<{ gap?: number }>`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: ${(props) => props.gap}px;
`;

const Row = styled.div<{ gap?: number }>`
  width: 100%;
  display: flex;
  flex-direction: row;
  gap: ${(props) => props.gap}px;
  align-items: center;
`;

const Row02 = styled.div<{ gap?: number }>`
  width: 100%;
  display: flex;
  flex-direction: row;
  gap: ${(props) => props.gap}px;
  align-items: center;
  justify-content: end;
`;
const Row03 = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-around;
  width: 100%;
`;
const Row04 = styled.div`
  display: flex;
  padding: 8px 16px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 4px;
  align-self: stretch;
  border-radius: 8px;
  background: #f7f8fc;
`;
const TitleBox = styled.div`
  position: relative;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
`;

const PageTitle = styled.div`
  font-family: Spoqa Han Sans Neo;
  font-size: 24px;
  font-weight: 700;
  line-height: 34.8px;
  color: #171e26;
`;

const Description = styled(Row)`
  width: 100%;
  gap: 8px;
  border-radius: 16px;
  background: #fff8f8;
  align-items: center;
  align-items: flex-start;
  padding: 24px;
  display: flex;
  flex-direction: column;
`;

const Sub_Description = styled(Box)`
  gap: 4px;
  padding-top: 12px;
  padding-right: 16px;
  padding-bottom: 12px;
  padding-left: 16px;
  border-radius: 8px;
  background: #70737c0d;
  width: fit-content;
  align-items: flex-start;
`;

const Text = styled.span<{ color: string; size: number; weight: number }>`
  font-family: Spoqa Han Sans Neo;
  font-weight: ${(props) => props.weight};
  font-size: ${(props) => props.size}px;
  line-height: 145%;
  letter-spacing: ${(props) => props.size * -0.02}px;
  text-align: center;
  color: ${(props) => props.color};
`;

const Item = styled.div`
  width: 384px;
  border-radius: 16px;
  padding: 16px;
  gap: 16px;
  border: 1px solid #e5e8ea;
  background: #ffffff;
`;

const NoThemeDescription = styled.div`
  height: 628 px;
  padding-top: 24px;
  padding-right: 16px;
  padding-bottom: 72px;
  padding-left: 16px;
  text-align: center;

  font-weight: 500;
  font-size: 14px;
  line-height: 145%;
  letter-spacing: -2%;
`;

const BadgeBox = styled.div`
  display: flex;
  flex-direction: row;
  gap: 8px;
`;

const PopoverBox = styled.div`
  position: fixid;
  top: 0;
  left: 0;
  background: #ffffff;
  box-shadow: 0px 4px 15px 0px #8d95a340;
  border-radius: 24px;
  padding: 8px 0;
  z-index: 10;
  width: 352px;
  border: 1px solid #e1e2e4;
  box-shadow:
    0px 0px 1px 0px #00000014,
    0px 1px 4px 0px #00000014,
    0px 2px 8px 0px #0000001f;
  cursor: default;
`;

const PopoverHeader = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding: 8px 16px;
  font-size: 16px;
  font-weight: 600;
  color: #222;
  img {
    background: none;
    border: none;
    font-size: 18px;
    font-weight: 700;
    cursor: pointer;
  }
`;

const AllBadgeWrap = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 8px;
`;

const BadgeWrapper = styled.div`
  position: relative;
`;

const TicketCountBadge = styled.div`
  display: flex;
  width: fit-content;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  padding: 7px 12px;
  gap: 16px;
  border-radius: 9999px;
  background-color: #70737c0d;

  p {
    font-weight: 700;
    font-size: 14px;
    color: #222222;
  }
`;

const ReportButton = styled.button<{ disable: boolean }>`
  font-weight: 700;
  font-size: 14px;
  line-height: 145%;
  letter-spacing: -2%;
  text-align: center;
  color: #008aff;
  cursor: ${({ disable }) => (disable ? 'default' : 'pointer')};
  opacity: ${({ disable }) => disable && 0.4};
`;

const Divider = styled.div`
  width: 100%;
  height: 1px;
  background-color: #e5e8ea;
`;

const DetailInfoTitle = styled.span`
  display: flex;
  font-weight: 500;
  font-size: 13px;
  line-height: 145%;
  letter-spacing: -2%;
  color: #616161;
  text-align: center;
  justify-content: center;
  span {
    font-weight: 700;
    color: #222;
  }
`;

const DetailInfoNumber = styled.div`
  display: flex;
  flex-direction: row;
  gap: 4px;
  font-weight: 700;
  font-size: 16px;
  line-height: 145%;
  letter-spacing: -2%;
  color: ${({ color }) => (color ? color : '#222')};
  justify-content: center;
`;
const DetailInfoNumber02 = styled.div<{ color?: string }>`
  display: flex;
  flex-direction: row;
  gap: 4px;
  font-weight: 700;
  font-size: 14px;
  line-height: 145%;
  letter-spacing: -2%;
  color: ${({ color }) => (color ? color : '#222')};
  justify-content: center;
`;
const MoreButton = styled.div`
  width: 100%;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 500;
  font-size: 12px;
  line-height: 40px;
  letter-spacing: -2%;
  text-align: center;
  color: #616161;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: row;
  gap: 2px;
  &:hover {
    background: #eaf2fe;
  }
  > img {
    width: 16px;
    height: 16px;
  }
`;

const PopoverContent = styled.div`
  width: 100%;
  padding: 8px 16px;
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;
